
import { Component, Vue } from 'vue-property-decorator';
import TicketRepositoryInterface, {
  TicketInterface, TicketPriorities,
  TicketReplyInterface, TicketScoreInterface,
  TicketWSMessageInterface,
} from '@/services/QA/ticket/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import EscalateDialog from '@/components/dialogs/qa/escalation/EscalateDialog.vue';
import FeedbackDialog from '@/components/dialogs/qa/feedback/FeedbackDialog.vue';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';

@Component({
  inject: [
    'ticketRepository',
    'userRepository',
    'profileRepository',
  ],
  components: {
    EscalateDialog,
    FeedbackDialog,
  },
  sockets: {
    ticketData(msg: TicketWSMessageInterface): void {
      this.$emit('ticketData', msg.ticket);
    },
  },
})
export default class ViewTicket extends Vue {
  private loading: boolean = true;
  private messageReceived: boolean = false;
  private responseReceived: boolean = false;
  private ticket: TicketInterface = {} as TicketInterface;
  private deletedScores: TicketScoreInterface[] = [];
  private ticketRepository!: TicketRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private usersMap: { [userId: string]: UserBaseInfoInterface } = {};
  private escalateDialog: string | false = false;
  private feedbackDialog: string | false = false;
  private ticketPriorities: Array<string | TicketPriorities>;

  public beforeCreate(): void {
    this.ticketPriorities = Object.values(TicketPriorities);
    this.$on('ticketData', async (ticket: TicketInterface) => {
      await this.getUsersInfoByEmails(ticket);
      this.messageReceived = true;
      this.mergeTicketProperties(ticket);
    });
  }

  public async created(): Promise<void> {
    const ticketId = Number(this.$route.params.ticketId);
    const ticket = await this.ticketRepository.getOneById(ticketId);
    if (!ticket.scores.length) {
      this.$errorDialog.show('Ticket cannot be viewed, because is not reviewed yet');
      await this.$router.push({ name: 'ticket_list' });
      return;
    }

    ticket.replies.map((reply) => {
      reply.commentInputVisible = false;
      return reply;
    });

    ticket.scores.forEach((ticketScore) => {
      if (ticketScore.score.deletedAt) {
        this.deletedScores.push(ticketScore);
      }
    });
    ticket.scores = ticket.scores.filter((ticketScore) => !ticketScore.score.deletedAt);

    await this.getUsersInfoByIds(ticket);
    this.ticket = ticket;
    this.responseReceived = true;
  }

  private async escalate(): Promise<void> {
    this.escalateDialog = 'escalate-dialog';
  }

  private async feedback(): Promise<void> {
    this.feedbackDialog = 'feedback-dialog';
  }

  private mergeTicketProperties(ticketData: TicketInterface): void {
    if (!this.responseReceived) {
      setTimeout(() => this.mergeTicketProperties(ticketData), 200);
      return;
    }

    if (ticketData.id !== this.ticket.id) {
      this.messageReceived = false;
      return;
    }

    Object.getOwnPropertyNames(ticketData).forEach((property: string) => {
      if (!this.ticket[property]) {
        this.ticket[property] = ticketData[property];
      } else if (Array.isArray(ticketData[property])) {
        (ticketData[property] as []).forEach((ticketDataArrItem: { id: number }) => {
          const index = (this.ticket[property] as [])
            .findIndex((value: { id: number }) => value.id === ticketDataArrItem.id);
          if (index !== -1) {
            Object.assign((this.ticket[property] as [])[index], ticketDataArrItem);
          } else {
            (this.ticket[property] as unknown[]).push(ticketDataArrItem);
          }
        });
      }
    });

    this.ticket.replies = this.ticket.replies.sort((replyA, replyB) => replyA.date.localeCompare(replyB.date));
    this.loading = !(this.responseReceived && this.messageReceived);
    this.$forceUpdate();
  }

  private async getUsersInfoByIds(ticket: TicketInterface): Promise<void> {
    const usersIds: string[] = [];

    ticket.comments.forEach((ticketComment) => {
      if (usersIds.indexOf(ticketComment.comment.authorId) === -1) {
        usersIds.push(ticketComment.comment.authorId);
      }
    });

    ticket.notes.forEach((ticketNote) => {
      if (usersIds.indexOf(ticketNote.note.authorId) === -1) {
        usersIds.push(ticketNote.note.authorId);
      }
    });

    ticket.scores.forEach((ticketScore) => {
      if (usersIds.indexOf(ticketScore.score.authorId) === -1) {
        usersIds.push(ticketScore.score.authorId);
      }
    });

    ticket.replies.forEach((reply) => {
      if (!reply.comments.length) {
        return;
      }

      reply.comments.forEach((comment) => {
        if (usersIds.indexOf(comment.comment.authorId) === -1) {
          usersIds.push(comment.comment.authorId);
        }
      });
    });

    const users = await this.userRepository.getUsersByIds(usersIds);
    users.forEach((user) => {
      this.usersMap[user.id] = user;
    });
  }

  private async getUsersInfoByEmails(ticket: TicketInterface): Promise<void> {
    ticket.agents = ticket.agents.filter((agent) => !!agent.email);
    const usersEmails: string[] = [];
    ticket.agents.forEach((agent) => {
      if (usersEmails.indexOf(agent.email) === -1) {
        usersEmails.push(agent.email);
      }
    });

    const users = await this.userRepository.getUsersByEmails(usersEmails);
    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(
      users.map((user) => user.id),
    );

    users.forEach((user) => {
      const profileIndex = profiles.findIndex((profile) => profile.userId === String(user.id));

      if (profileIndex === -1) {
        return;
      }

      this.usersMap[user.id] = user;
      const index = ticket.agents.findIndex((agent) => agent.email === user.email);
      if (index !== -1) {
        ticket.agents[index].id = user.id;
        ticket.agents[index].fullName =
          `${profiles[profileIndex].firstName} ${profiles[profileIndex].lastName}`;
      }
    });
  }

  private async closeReview(): Promise<void> {
    await this.$router.push({ path: '/qc/tickets' });
  }
}
